import {
  clamp01,
  lerp,
  lerpRadians,
  lerpVector3,
} from "../core/math.ts";
import type { RoadVehiclePresentation } from "../sim/vehicles/index.ts";

export function copyRoadVehiclePresentations(
  presentations: readonly Readonly<RoadVehiclePresentation>[],
): readonly RoadVehiclePresentation[] {
  return presentations.map((vehicle) => ({
    ...vehicle,
    position: { ...vehicle.position },
  }));
}

export function interpolateRoadVehiclePresentations(
  previous: readonly Readonly<RoadVehiclePresentation>[],
  current: readonly Readonly<RoadVehiclePresentation>[],
  interpolationAlpha: number,
): readonly RoadVehiclePresentation[] {
  const alpha = clamp01(interpolationAlpha);
  const previousById = new Map(
    previous.map((vehicle) => [vehicle.id, vehicle] as const),
  );

  return current.map((vehicle) => {
    const from = previousById.get(vehicle.id);
    if (from === undefined) {
      return {
        ...vehicle,
        position: { ...vehicle.position },
      };
    }
    return {
      ...vehicle,
      position: lerpVector3(from.position, vehicle.position, alpha),
      yawRadians: lerpRadians(from.yawRadians, vehicle.yawRadians, alpha),
      pitchRadians: lerpRadians(
        from.pitchRadians,
        vehicle.pitchRadians,
        alpha,
      ),
      rollRadians: lerpRadians(
        from.rollRadians,
        vehicle.rollRadians,
        alpha,
      ),
      speedMps: lerp(from.speedMps, vehicle.speedMps, alpha),
      steeringRadians: lerp(
        from.steeringRadians,
        vehicle.steeringRadians,
        alpha,
      ),
      longitudinalAccelerationMps2: lerp(
        from.longitudinalAccelerationMps2,
        vehicle.longitudinalAccelerationMps2,
        alpha,
      ),
      wheelRotationRadians: lerpRadians(
        from.wheelRotationRadians,
        vehicle.wheelRotationRadians,
        alpha,
      ),
      visualPitchRadians: lerpRadians(
        from.visualPitchRadians,
        vehicle.visualPitchRadians,
        alpha,
      ),
      visualRollRadians: lerpRadians(
        from.visualRollRadians,
        vehicle.visualRollRadians,
        alpha,
      ),
      suspensionCompressionM: lerp(
        from.suspensionCompressionM,
        vehicle.suspensionCompressionM,
        alpha,
      ),
      doorOpen01: lerp(from.doorOpen01, vehicle.doorOpen01, alpha),
      impactIntensity01: lerp(
        from.impactIntensity01,
        vehicle.impactIntensity01,
        alpha,
      ),
      damage01: lerp(from.damage01, vehicle.damage01, alpha),
    };
  });
}
